/*
 * assets/sofsy-quiet.js: quiet mode, a staff device that stops sending tracking (zao
 * domains/sofsy/01-sales-marketing/shopify/analytics/DESIGN_2026-10-03_INTERNAL_TRAFFIC_MARKING.md, section 3
 * "New candidate B" and layer 4; ref zao#617, zao#612).
 *
 * Turning it on, once per device. The secret quiet code NEVER travels in an address: any address a staff browser opens
 * is sent, in full, to the Google app's GA4, Shopify's own analytics and klaviyo.js before this file can act.
 *   - Chrome: the sofsy Quiet extension (zao benmay-io/tools/quiet-extension/) writes the quiet mark below itself, on
 *     every sofsy.com page and from its background worker. No link.
 *   - A phone or any other browser: open https://sofsy.com/?sofsy_quiet_setup=1 (no secret in it). Only with that
 *     exact parameter, this file shows a small "Staff only" box at the top of the screen (nothing is visible otherwise).
 *     It is a modal <dialog> (showModal()): drawn in the top layer, and the rest of the page, Consentmo's cookie banner
 *     included, is inert while it is open. Consentmo's banner is a top-layer popover whose full-screen overlay took
 *     every tap in Safari in Bangkok, even over a top-layer popover (live probe, 7 Oct 2026); a modal dialog keeps the
 *     field usable. Consentmo also sets 'inert' on everything else on the page to trap the focus in its banner: for
 *     2 min the box takes 'inert' off itself at once, and is shown again whenever that happens or a popover opens after
 *     it (Consentmo shows, and re-shows, its banner), so it stays on top and usable; the banner can be answered once
 *     the box is closed. A Close button, or Escape, shuts it. Escape goes no further: it is caught on the window in the
 *     capture phase, because Consentmo answers its banner with "accept all" on Escape. It shows itself again at most 20 times, and never while the person is typing. Where showModal() is missing, it is a fixed panel at the top, clear
 *     of a banner at the bottom. It says what Shopify confirmed, not only that the mark is there: after the code
 *     "Code accepted; saving "no" to tracking...", and on a later visit "quiet (confirmed)" (the confirmation cookie
 *     below) or "Marked, but sofsy.com has not confirmed "no" yet". The panel's content sits in a closed shadow root
 *     where the browser supports one: no script can find the field through the page's DOM, but a
 *     script listening for key presses on the whole document could still rebuild what is typed, so the box is a
 *     guard against casual reads, not against a hostile script on the page. Its host carries an inline
 *     'display: block !important', because the theme's base.css hides 'div:empty' and a host whose content is all
 *     in a shadow root counts as empty. The set-up page itself is held back like a quiet page: its GA4 hits carry
 *     traffic_type=internal and bundle.js's gates read "no" (steps 0 and 1 below). Paste or type the code and tap the
 *     button: this file hashes it locally (SHA-256) and compares it with QUIET_HASH. On a match it writes the mark,
 *     writes the quiet consent (Consentmo's answer, then Shopify's, as in step 2; one retry if Shopify reports an
 *     error), and reloads the page without '?sofsy_quiet_setup' and without any '?sofsy_internal' as soon as Shopify
 *     confirms, or after 3 s whatever happened, so the next page is normally already "no". There is no <form>: the
 *     code is never put in an address, in storage or in any request, and the field is emptied at once.
 *   The code is never in this repository: this file holds only its SHA-256 (QUIET_HASH). It is 24 bytes from a CSPRNG
 *   (192 bits, 32 characters of [A-Za-z0-9_-]), so the public hash cannot be reversed by search. Making or rotating it:
 *   zao benmay-io/tools/quiet-extension/make-quiet-secret.mjs (writes the code to a private file, prints only the hash).
 *   '?sofsy_internal=<the code>' does nothing: the code is not accepted from an address.
 *   - The old plain link '?sofsy_internal=quiet' keeps working until LEGACY_QUIET_UNTIL (Thu 8 Oct 2026 12:00 Bangkok,
 *     05:00 UTC; moved forward from Sat 10 Oct by MASTER), then does nothing. Why: a forwarded '=quiet' link marked whoever
 *     opened it (a real shopper too) internal and quiet for 400 days, and 'quiet' is guessable (the #159 audit,
 *     follow-up 1). The grace only limits the link: a browser that turned quiet through it before the cut-off holds
 *     the current mark and stays quiet afterwards, until the next rotation.
 * The mark, a versioned value:
 *   - cookie sofsy_quiet=<QUIET_GENERATION> (now 'g1') and its localStorage mirror 'sofsy:quiet' = the same value;
 *   - the internal mark too (cookie sofsy_internal=1 and mirror 'sofsy:internal' = '1'), the same name and attributes
 *     as assets/sofsy-track.js (path /, SameSite Lax, 400 days, Secure on https), so a quiet browser is always internal.
 *   Only the current generation counts. Rotating the code bumps QUIET_GENERATION, so every older mark is ignored and
 *   the rotation really revokes it. The bare value '1' that storefront #159 wrote counts until LEGACY_QUIET_UNTIL
 *   (re-stamped as the current generation on the next page), then is ignored.
 * Turning it off: https://sofsy.com/?sofsy_internal=0 clears both marks (sofsy-track.js clears the internal one, this
 * file the quiet one, any generation) and, in a browser that was quiet, calls Consentmo.reset() when Consentmo is on
 * the page. In Consentmo's banner regions (Thailand among them) Consentmo then treats her as undecided, so tracking
 * resumes from the next page; elsewhere Shopify's "no" stays until site data for sofsy.com is cleared. sofsy-track.js
 * takes the parameter out of the address bar, for every value, so it is not copied onward with the page's address.
 * A browser with the extension installed is marked again on its next sofsy.com page: '=0' is for a browser without it.
 *
 * The consent quiet mode wants: "no" to analytics, marketing and sale of data; preferences stay allowed (Ben's
 * decision, 6 Oct): they are not tracking, and staff keep the Gorgias chat and Reviews.io.
 *
 * What it does, on every page in a quiet browser (and nowhere else):
 *   1. Before bundle.js runs (this file is deferred, ahead of it), sets window.__consentState =
 *      {analytics: false, functional: true, ads: false}. bundle.js's hasConsent() reads it: 'analytics' false, so
 *      bundle.js never loads Clarity; 'functional' true, so the Gorgias chat facade and Reviews.io load as usual;
 *      'ads' false, so bundle.js's Klaviyo form openers (the kform-* elements) do nothing when tapped.
 *   2. Checks the visitor's consent, and corrects it when it is not quiet, on EVERY page (no once-per-session rule):
 *      Consentmo (the cookie banner app, window.Consentmo) pushes its own answer to Shopify on every page, about 1 to
 *      2 s after load; a visitor who once tapped Accept, or a region's default, would undo a one-off correction.
 *        - Consentmo on the page: quiet mode answers Consentmo itself, through its own API,
 *            window.Consentmo.setTrackingConsent({analytics: false, marketing: false, preferences: true})
 *          ("accept_selected"), whenever Consentmo.analyticsAllowed() or marketingAllowed() is true, whatever
 *          getStatus() says. Consentmo then pushes that answer to Shopify, on this page and every later one. Never
 *          declineAll(): that also says "no" to preferences. After an answer, no second one until Consentmo has
 *          pushed (a visitorConsentCollected event) or 2 s have passed, so Consentmo records no duplicates.
 *          Consentmo's push carries only analytics, marketing and preferences, so once Consentmo is quiet and
 *          Shopify's sale_of_data is still not "no", one direct Shopify write (below) sets it.
 *        - No Consentmo (after waiting 2 s for it): Shopify's Customer Privacy API directly,
 *            Shopify.customerPrivacy.setTrackingConsent({analytics: false, marketing: false, preferences: true,
 *                                                        sale_of_data: false}, callback)
 *          unless analytics, marketing and sale of data are already "no". Also the fallback when Consentmo refuses.
 *      The check runs when the API or Consentmo arrives, on every visitorConsentCollected event (Shopify's "consent
 *      changed"), and every 250 ms for 10 s, so a late Consentmo push is corrected. At most 3 corrections per page
 *      (Consentmo answers and direct writes together), so two scripts can never ping-pong. Nothing is recorded as done
 *      before Shopify or Consentmo confirms it; a failure is logged and retried by the next check, and a write that
 *      never confirms stops blocking the checks after 3 s. Quiet means Shopify reads "no" to analytics, marketing and
 *      sale of data (and, with Consentmo, Consentmo says "no" to analytics and marketing).
 *      With "no", Shopify withholds customer events from every pixel that declares analytics, marketing or sale of
 *      data (Meta, Klaviyo, Shoplift, Pinterest, our "sofsy events" chopsticks pixel and the "sofsy" custom pixel),
 *      and the theme's own gates read "no" (sofsy-track.js: Clarity events and the exit beacon; cart.js and
 *      footer-signup.js: Meta ids).
 *   3. Logs '[sofsy] quiet mode on (...)' once, after Shopify or Consentmo confirms (or with what went wrong), and one
 *      line per later correction. No visible change on the page: the theme has no pattern for a staff-only cue.
 *   4. Once Shopify reads "no" to analytics, marketing and sale of data on this page, writes the readable
 *      confirmation cookie sofsy_quiet_ok=<QUIET_GENERATION> (path /, SameSite Lax, 400 days, Secure on https). Shopify
 *      keeps its own answer in an httpOnly cookie that nothing else can read, so this is what the sofsy Quiet
 *      extension's check page reads to say QUIET ON. It is deleted again whenever it would be stale: by
 *      '?sofsy_internal=0' with the marks, on any page that is not quiet (a rotated generation, the old '1' after the
 *      cut-off), and on a quiet page as soon as Shopify or Consentmo reads anything but "no" (until "no" is back).
 *   0. (Before 1; also on the set-up page.) Queues gtag('set', {traffic_type: 'internal'}) on window.dataLayer, so
 *      every GA4 hit this page sends carries traffic_type=internal (no IP rules). GA4 removes those hits only once its
 *      Internal Traffic data filter is set to Active, which waits on Ben (zao#659 D2); until then they are marked, not
 *      removed. Measured 7 Oct 2026 (zao#659): with this queued, 8 of 8 GA4 hits on the first quiet page carried
 *      tt=internal; Google Ads and Merchant Center hits are unchanged.
 *
 * What it does NOT do:
 *   - It does not hold back what a page sends before the consent write lands: on the first quiet page (the
 *     '?sofsy_internal=quiet' link, the set-up page, or the extension's first page in a fresh profile) and on any page
 *     before Consentmo or Shopify confirms "no". There, Shopify's page_viewed (it fires before this file runs) and the
 *     page's address reach Shopify's analytics; the Google app's GA4 hits go out, marked traffic_type=internal (step
 *     0), and its Google Ads hits go out unmarked; klaviyo.js sends its own page view; requests the page makes before
 *     sofsy-track.js cleans the address carry it in a same-origin Referer; and pixels that declare analytics or
 *     marketing still receive that page's events when the visitor's consent was already "yes" before the write. On
 *     the set-up page, measured on live sofsy.com on 7 Oct 2026 (zao#612): our chopsticks pixel's page_viewed (not
 *     marked internal, consent "yes"), one Merchant Center hit, a stats.g.doubleclick.net ping without the internal
 *     mark, Pinterest (32 requests through Shopify's web pixel, consent still undecided) and, in Safari in Bangkok,
 *     Meta's /tr beacon also go out. The set-up page waits for Shopify's "no" before it reloads (or 3 s), so the page
 *     after it normally starts quiet. None of these
 *     addresses carries the code: it is never accepted from an address.
 *   - The Google & YouTube app (GA4 G-9YTEQXNN0Z, Google Ads AW-11378106807) declares no privacy purposes, yet on
 *     7 Oct 2026 Shopify did not load it at all once consent read "no" (0 GA4 and 0 Google Ads hits on quiet pages 2
 *     and 3). On the page before that, its GA4 hits are marked internal (step 0) but its Google Ads hits are not; Google
 *     Ads only counts a conversion it can attribute to an ad click, so staff must never click a sofsy ad.
 *   - It does not stop Shopify's own analytics. Shopify's Live View help page says a consent banner "can prevent
 *     third-party analytics from tracking visitors who don't consent, whereas Shopify's tracking isn't affected"
 *     (https://help.shopify.com/en/manual/reports-and-analytics/shopify-reports/live-view). Shopify's documented way to
 *     view the store without counting a session in its acquisition reports is admin's "View store" on desktop
 *     (https://help.shopify.com/en/manual/reports-and-analytics/discrepancies/customer-discrepancies).
 *   - It does not stop klaviyo.js's own on-site tracking (a.klaviyo.com/client/...) or sofsy-track.js's Klaviyo
 *     "Added to Cart" (_learnq), which the theme does not gate (sofsy-track.js header, sink 3); only Klaviyo's pixel is
 *     withheld by Shopify.
 *   - It never affects a browser without the quiet mark. Nothing but the code typed into the set-up box (its SHA-256
 *     must match), '?sofsy_internal=quiet' before LEGACY_QUIET_UNTIL, or an existing current mark (the cookie, or its
 *     mirror restoring a cookie Safari expired) turns it on: not the code in an address, not '?sofsy_internal=1', not
 *     the internal cookie, not a logged-in staff account, not Shopify admin, not the theme editor. In a browser without
 *     the mark (a normal shopper, or '?sofsy_internal=0' in a browser that was never quiet) and without
 *     '?sofsy_quiet_setup=1', this file reads the address, one cookie and one localStorage key, writes no cookie and no
 *     storage, adds nothing to the page, calls no consent API and loads nothing; its only trace is
 *     window.sofsyQuiet = {on: false}.
 * '?sofsy_internal=0' never grants consent itself (a script must never grant consent for a person): see "Turning it
 * off" above for what happens next.
 */
(() => {
  'use strict';

  if (window.sofsyQuiet) return;

  const PARAM = 'sofsy_internal'; // the same parameter as the internal mark (assets/sofsy-track.js)
  // SHA-256 hex of the secret quiet code (the code itself is never in the repository; header, "Turning it on").
  const QUIET_HASH = '213713f8225a001c35a03a167b556f081142838ad8e1f4ec7c9826cd47f62acc';
  const CODE_SHAPE = /^[A-Za-z0-9_-]{32}$/; // only a value of this shape is hashed
  // The mark's current generation. Bump it (g2, g3 ...) whenever QUIET_HASH changes, so older marks stop counting.
  const QUIET_GENERATION = 'g1';
  const SETUP_PARAM = 'sofsy_quiet_setup'; // '?sofsy_quiet_setup=1' shows the code box; not a secret
  const SETUP_SETTLE_MS = 3000; // after the code: reload when Shopify confirms "no", or after this long
  const RAISE_TICKS = 480; // the set-up box watches for popovers opened after it for 480 x 250 ms = 2 min
  const MAX_SHOWS = 20; // ... and shows itself again at most 20 times
  const LEGACY_QUIET_VALUE = 'quiet'; // the old plain link: counted only until LEGACY_QUIET_UNTIL
  const LEGACY_MARK = '1'; // the bare mark storefront #159 wrote: counts until the same cut-off
  const LEGACY_QUIET_UNTIL = Date.UTC(2026, 9, 8, 5, 0, 0); // Thu 8 Oct 2026 05:00 UTC = 12:00 Bangkok
  const QUIET_COOKIE = 'sofsy_quiet';
  const QUIET_MIRROR = 'sofsy:quiet';
  const OK_COOKIE = 'sofsy_quiet_ok'; // the readable "Shopify says no" confirmation (header, step 4)
  const INTERNAL_COOKIE = 'sofsy_internal'; // assets/sofsy-track.js INTERNAL_COOKIE
  const INTERNAL_MIRROR = 'sofsy:internal'; // assets/sofsy-track.js INTERNAL_MIRROR
  const MAX_AGE = 34560000; // 400 days, as sofsy-track.js INTERNAL_MAX_AGE
  const WATCH_MS = 10000; // how long each page keeps checking (Consentmo pushes about 1 to 2 s after load)
  const POLL_MS = 250;
  const CONSENTMO_GRACE_MS = 2000; // wait this long for Consentmo before writing Shopify's consent directly
  const MAX_FIXES = 3; // corrections per page: never an endless ping-pong with another script
  const CONSENTMO_SETTLE_MS = 2000; // after answering Consentmo, wait for its push (or this long) before answering again
  const IN_FLIGHT_MAX_MS = 3000; // a consent write that never confirms stops blocking the checks after this long
  // "No" to tracking; preferences (chat, reviews) stay allowed: not tracking (Ben's decision, 6 Oct).
  const QUIET_CONSENT = Object.freeze({ analytics: false, marketing: false, preferences: true, sale_of_data: false });
  const CONSENTMO_ANSWER = Object.freeze({ analytics: false, marketing: false, preferences: true });

  const readCookie = (name) => {
    try {
      const pair = document.cookie.split('; ').find((part) => part.indexOf(`${name}=`) === 0);
      return pair ? decodeURIComponent(pair.slice(name.length + 1)) : null;
    } catch (_) {
      return null;
    }
  };
  const readMirror = (key) => {
    try {
      return window.localStorage.getItem(key);
    } catch (_) {
      return null;
    }
  };
  // The same cookie attributes as sofsy-track.js writeMark(). value null deletes the cookie and the mirror.
  const writeMark = (cookie, mirror, value) => {
    try {
      const secure = window.location.protocol === 'https:' ? '; Secure' : '';
      document.cookie = value
        ? `${cookie}=${value}; path=/; SameSite=Lax; max-age=${MAX_AGE}${secure}`
        : `${cookie}=; path=/; SameSite=Lax; max-age=0${secure}`;
    } catch (_) {
      /* the mirror may still hold it */
    }
    try {
      if (value) window.localStorage.setItem(mirror, value);
      else window.localStorage.removeItem(mirror);
    } catch (_) {
      /* the cookie may still hold it */
    }
  };
  // The confirmation cookie (header, step 4): only a cookie, no mirror.
  const writeOk = (on) => {
    try {
      const secure = window.location.protocol === 'https:' ? '; Secure' : '';
      document.cookie = on
        ? `${OK_COOKIE}=${QUIET_GENERATION}; path=/; SameSite=Lax; max-age=${MAX_AGE}${secure}`
        : `${OK_COOKIE}=; path=/; SameSite=Lax; max-age=0${secure}`;
    } catch (_) {
      /* the check page then says "not confirmed" */
    }
  };
  const markQuiet = () => {
    writeMark(QUIET_COOKIE, QUIET_MIRROR, QUIET_GENERATION); // re-stamped on every page view (Safari's 7-day cap)
    writeMark(INTERNAL_COOKIE, INTERNAL_MIRROR, '1'); // quiet is always internal
  };

  const consentmo = () => {
    try {
      const C = window.Consentmo;
      return C &&
        typeof C.setTrackingConsent === 'function' &&
        typeof C.analyticsAllowed === 'function' &&
        typeof C.marketingAllowed === 'function'
        ? C
        : null;
    } catch (_) {
      return null;
    }
  };
  const shopifyApi = () => {
    try {
      const api = window.Shopify && window.Shopify.customerPrivacy;
      return api && typeof api.setTrackingConsent === 'function' && typeof api.currentVisitorConsent === 'function'
        ? api
        : null;
    } catch (_) {
      return null;
    }
  };

  // Poll every POLL_MS until done() is true or WATCH_MS has passed; then last() once.
  const watch = (tick, last) => {
    const started = Date.now();
    if (tick()) return;
    const timer = setInterval(() => {
      let stop = false;
      try {
        stop = tick();
      } catch (_) {
        stop = true;
      }
      if (stop || Date.now() - started > WATCH_MS) {
        clearInterval(timer);
        if (!stop && last) last();
      }
    }, POLL_MS);
  };

  // SHA-256 of an ASCII string, as lowercase hex (FIPS 180-4). Synchronous on purpose: window.sofsyQuiet must be decided
  // before bundle.js runs, and crypto.subtle is asynchronous. Only ever given a CODE_SHAPE value (32 ASCII characters).
  const K = [
    0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5, 0xd807aa98,
    0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174, 0xe49b69c1, 0xefbe4786,
    0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da, 0x983e5152, 0xa831c66d, 0xb00327c8,
    0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967, 0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13,
    0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85, 0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819,
    0xd6990624, 0xf40e3585, 0x106aa070, 0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a,
    0x5b9cca4f, 0x682e6ff3, 0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7,
    0xc67178f2,
  ];
  const sha256Hex = (text) => {
    const bytes = [];
    for (let i = 0; i < text.length; i += 1) bytes.push(text.charCodeAt(i) & 0xff);
    const bitLength = bytes.length * 8;
    bytes.push(0x80);
    while (bytes.length % 64 !== 56) bytes.push(0);
    for (let shift = 56; shift >= 0; shift -= 8) bytes.push(shift >= 32 ? 0 : (bitLength >>> shift) & 0xff);
    const h = [0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19];
    const w = new Array(64);
    const rotr = (x, n) => (x >>> n) | (x << (32 - n));
    for (let block = 0; block < bytes.length; block += 64) {
      for (let i = 0; i < 16; i += 1) {
        const j = block + i * 4;
        w[i] = ((bytes[j] << 24) | (bytes[j + 1] << 16) | (bytes[j + 2] << 8) | bytes[j + 3]) | 0;
      }
      for (let i = 16; i < 64; i += 1) {
        const s0 = rotr(w[i - 15], 7) ^ rotr(w[i - 15], 18) ^ (w[i - 15] >>> 3);
        const s1 = rotr(w[i - 2], 17) ^ rotr(w[i - 2], 19) ^ (w[i - 2] >>> 10);
        w[i] = (w[i - 16] + s0 + w[i - 7] + s1) | 0;
      }
      let [a, b, c, d, e, f, g, k] = h;
      for (let i = 0; i < 64; i += 1) {
        const t1 = (k + (rotr(e, 6) ^ rotr(e, 11) ^ rotr(e, 25)) + ((e & f) ^ (~e & g)) + K[i] + w[i]) | 0;
        const t2 = ((rotr(a, 2) ^ rotr(a, 13) ^ rotr(a, 22)) + ((a & b) ^ (a & c) ^ (b & c))) | 0;
        k = g;
        g = f;
        f = e;
        e = (d + t1) | 0;
        d = c;
        c = b;
        b = a;
        a = (t1 + t2) | 0;
      }
      [a, b, c, d, e, f, g, k].forEach((v, i) => {
        h[i] = (h[i] + v) | 0;
      });
    }
    return h.map((v) => (v >>> 0).toString(16).padStart(8, '0')).join('');
  };
  const isCode = (text) => CODE_SHAPE.test(text) && sha256Hex(text) === QUIET_HASH;
  const beforeCutOff = () => Date.now() < LEGACY_QUIET_UNTIL;
  // A mark value that counts: the current generation, or the bare '1' before the cut-off.
  const counts = (value) => value === QUIET_GENERATION || (value === LEGACY_MARK && beforeCutOff());

  // Is this browser quiet? Writes only for '=quiet' before the cut-off, a mark that counts, or '=0' in a browser that
  // holds any quiet mark.
  let resetConsentmo = false;
  let params = null;
  const decide = () => {
    try {
      params = new URL(window.location.href).searchParams;
    } catch (_) {
      /* no address: only the existing mark counts */
    }
    const param = params ? params.get(PARAM) : null;
    const cookieValue = readCookie(QUIET_COOKIE);
    const mirrorValue = readMirror(QUIET_MIRROR);
    if (param === '0') {
      if (cookieValue || mirrorValue || readCookie(OK_COOKIE)) {
        writeMark(QUIET_COOKIE, QUIET_MIRROR, null);
        writeOk(false);
        resetConsentmo = true;
      }
      return false;
    }
    if (counts(cookieValue) || counts(mirrorValue) || (param === LEGACY_QUIET_VALUE && beforeCutOff())) {
      markQuiet();
      return true;
    }
    // Not quiet (a rotated generation, the old '1' after the cut-off): a confirmation left over must not say so.
    if (readCookie(OK_COOKIE) !== null) writeOk(false);
    return false;
  };

  let quiet = false;
  try {
    quiet = decide();
  } catch (_) {
    quiet = false;
  }
  window.sofsyQuiet = Object.freeze({ on: quiet });

  // The open set-up box's label follows the confirmation live (header, step 4); set by showSetup() on a quiet page.
  let setupLabel = null;
  const setup = (() => {
    try {
      return !!params && params.get(SETUP_PARAM) === '1';
    } catch (_) {
      return false;
    }
  })();

  // Steps 0 and 1 (header): on a quiet page, and on the set-up page.
  const holdBack = () => {
    // 0. GA4 backstop (ref zao#659 D2): every GA4 hit from this page carries traffic_type=internal ('tt=internal' on
    // /g/collect), so GA4's Internal Traffic data filter can drop it once Active. Needed for the pages Shopify's
    // consent cannot hold back: the first quiet page (the link, the set-up page, or the extension's first page in a
    // fresh profile) and any page where Shopify's consent is not yet "no". The Google & YouTube app pixel runs gtag in
    // this window and Shopify loads it after this deferred file, so the queued 'set' is processed before its config
    // and page_view. Pushed as an Arguments object, exactly as gtag() does. GA4 only: Google Ads, Merchant Center and
    // Meta ignore the parameter.
    try {
      window.dataLayer = window.dataLayer || [];
      (function gtagSet() {
        window.dataLayer.push(arguments);
      })('set', { traffic_type: 'internal' });
    } catch (_) {
      /* GA4 counts this page; Shopify's consent below still holds back the later ones */
    }
    // 1. bundle.js's own gates.
    try {
      window.__consentState = { analytics: false, functional: QUIET_CONSENT.preferences, ads: false };
    } catch (_) {
      /* Clarity may load; Shopify's answer below still holds back the pixels */
    }
  };

  // After a matching code on the set-up page: the quiet consent first (Consentmo's answer, then Shopify's direct
  // write, which also carries sale_of_data), then the reload, in Shopify's callback or after SETUP_SETTLE_MS.
  const settleThenReload = (reload) => {
    let finished = false;
    let asked = false;
    const finish = () => {
      if (finished) return;
      finished = true;
      reload();
    };
    const C = consentmo();
    if (C) {
      try {
        const pending = C.setTrackingConsent(Object.assign({}, CONSENTMO_ANSWER));
        if (pending && typeof pending.catch === 'function') pending.catch(() => {});
      } catch (_) {
        /* Shopify's direct write below still says "no" */
      }
    }
    const started = Date.now();
    let attempts = 0;
    let waiting = false;
    const failed = () => {
      waiting = false; // a failure never reloads early: one retry on the next tick, else the 3 s fallback
    };
    watch(
      () => {
        if (finished) return true;
        const api = shopifyApi();
        if (api && !waiting && attempts < 2) {
          attempts += 1;
          waiting = true;
          try {
            api.setTrackingConsent(Object.assign({}, QUIET_CONSENT), (result) => {
              if (result && result.error) failed();
              else finish();
            });
          } catch (_) {
            failed();
          }
        }
        if (!finished && Date.now() - started >= SETUP_SETTLE_MS) finish();
        return finished;
      },
      finish
    );
  };

  // '?sofsy_quiet_setup=1': the staff code box (header, "Turning it on"). The code stays in this function: never in an
  // address, storage, a request or the console.
  const showSetup = () => {
    const el = (tag, props) => Object.assign(document.createElement(tag), props || {});
    // The panel is a MODAL <dialog> at the top of the screen. showModal() puts it in the top layer AND makes the rest
    // of the page inert, Consentmo's cookie banner included: in Safari in Bangkok the banner's full-screen overlay took
    // every tap, even over a top-layer popover (live probe, 7 Oct 2026). The top also clears a banner at the bottom and
    // the half of the screen a phone keyboard covers. Where showModal() is missing, it is a fixed panel at the top.
    const dialog = el('dialog', { id: 'sofsy-quiet-setup-dialog' });
    dialog.setAttribute('aria-label', 'Quiet mode set-up');
    dialog.setAttribute(
      'style',
      'display: block !important; position: fixed !important; inset: 16px 16px auto 16px !important; ' +
        'margin: 0 auto !important; max-width: 420px; width: auto; height: auto; padding: 0 !important; ' +
        'border: 0 !important; background: transparent !important; overflow: visible !important; ' +
        'pointer-events: auto !important; z-index: 2147483647'
    );
    const host = el('div', { id: 'sofsy-quiet-setup' });
    // base.css hides 'div:empty'; with its content in a shadow root the host is empty, so force it shown.
    host.setAttribute('style', 'display: block !important');
    // A closed shadow root, where supported: other scripts on the page cannot reach the field through the DOM.
    let root = host;
    try {
      if (typeof host.attachShadow === 'function') root = host.attachShadow({ mode: 'closed' });
    } catch (_) {
      root = host;
    }
    const box = el('div', {});
    box.style.cssText =
      'padding:16px;background:#fff;color:#111;border:1px solid #bbb;border-radius:8px;font:15px/1.4 sans-serif;' +
      'box-shadow:0 4px 16px rgba(0,0,0,.2)';
    // D3: say what Shopify confirmed, not only that the mark is there.
    let labelText = 'Staff only. Paste the quiet code.';
    if (quiet) {
      labelText =
        readCookie(OK_COOKIE) === QUIET_GENERATION
          ? 'Staff only. This browser is quiet (confirmed). To renew it, paste the quiet code.'
          : 'Staff only. Marked, but sofsy.com has not confirmed "no" yet: reload, or tell MASTER. To renew it, paste the quiet code.';
    }
    const label = el('label', { textContent: labelText });
    if (quiet) {
      setupLabel = (confirmed) => {
        label.textContent = confirmed
          ? 'Staff only. This browser is quiet (confirmed). To renew it, paste the quiet code.'
          : 'Staff only. Marked, but sofsy.com has not confirmed "no" yet: reload, or tell MASTER. To renew it, paste the quiet code.';
      };
    }
    label.setAttribute('for', 'sofsy-quiet-code');
    const input = el('input', { id: 'sofsy-quiet-code', type: 'password' });
    // 'one-time-code', never 'current-password': a password manager must not offer or save it as the sofsy.com login.
    ['autocomplete:one-time-code', 'autocapitalize:off', 'autocorrect:off', 'spellcheck:false'].forEach((pair) => {
      const [name, value] = pair.split(':');
      input.setAttribute(name, value);
    });
    input.style.cssText = 'display:block;width:100%;margin:8px 0;padding:10px;font-size:16px;box-sizing:border-box';
    const button = el('button', { type: 'button', textContent: 'Turn on quiet mode' });
    button.style.cssText = 'padding:10px 14px;font-size:15px';
    const closeButton = el('button', { type: 'button', textContent: 'Close' });
    closeButton.style.cssText = 'padding:10px 14px;font-size:15px;margin-left:8px;background:none;border:0;text-decoration:underline';
    const note = el('p', { textContent: '' });
    note.setAttribute('role', 'status');
    let timer = null;
    const submit = () => {
      const text = String(input.value || '').trim();
      input.value = '';
      if (!isCode(text)) {
        note.textContent = 'That is not the quiet code. Nothing was saved.';
        return;
      }
      markQuiet();
      note.textContent = 'Code accepted; saving "no" to tracking...';
      settleThenReload(() => {
        try {
          params.delete(SETUP_PARAM);
          params.delete(PARAM); // never reload into '?sofsy_internal=0' (it would turn quiet off again)
          const query = params.toString();
          window.location.replace(`${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash || ''}`);
        } catch (_) {
          /* the mark is written; the next page is quiet */
        }
      });
    };
    let observer = null;
    let gone = false;
    let goneAt = 0;
    const shut = () => {
      gone = true;
      goneAt = Date.now();
      if (timer) clearInterval(timer);
      timer = null;
      try {
        if (observer) observer.disconnect();
      } catch (_) {
        /* nothing left to watch */
      }
      // A closed box must not stay drawn: its inline 'display: block !important' goes first.
      try {
        dialog.setAttribute('style', 'display: none !important');
      } catch (_) {
        /* removed below anyway */
      }
      try {
        if (typeof dialog.close === 'function' && dialog.open) dialog.close();
      } catch (_) {
        /* removed below anyway */
      }
      try {
        dialog.remove();
      } catch (_) {
        /* gone */
      }
      setupLabel = null;
    };
    button.addEventListener('click', submit);
    closeButton.addEventListener('click', shut);
    // Escape closes the box, and goes no further: Consentmo answers its banner with "accept all" on Escape (live
    // probe, 7 Oct 2026). Caught on the window in the capture phase, registered before Consentmo loads, so no other
    // listener sees the Escape keydown, keypress or keyup; the keyup that follows the closing keydown is swallowed too
    // (for 1 s after the box is shut). The dialog's own 'cancel' (Escape, or Android's back gesture) also only shuts it.
    const swallowEscape = (event) => {
      if (!event || event.key !== 'Escape') return;
      if (gone && Date.now() - goneAt > 1000) {
        ['keydown', 'keypress', 'keyup'].forEach((type) => window.removeEventListener(type, swallowEscape, true));
        return;
      }
      if (typeof event.stopImmediatePropagation === 'function') event.stopImmediatePropagation();
      if (typeof event.preventDefault === 'function') event.preventDefault();
      if (event.type === 'keydown' && !gone) shut();
    };
    try {
      ['keydown', 'keypress', 'keyup'].forEach((type) => window.addEventListener(type, swallowEscape, true));
    } catch (_) {
      /* Escape then reaches the page as before */
    }
    dialog.addEventListener('cancel', (event) => {
      if (event && typeof event.preventDefault === 'function') event.preventDefault();
      shut();
    });
    input.addEventListener('keydown', (event) => {
      if (event && event.key === 'Enter') {
        if (typeof event.preventDefault === 'function') event.preventDefault();
        submit();
      }
    });
    [label, input, button, closeButton, note].forEach((node) => box.appendChild(node));
    root.appendChild(box);
    dialog.appendChild(host);
    document.body.appendChild(dialog);
    // Into the top layer as a modal; again whenever a popover opens after it (Consentmo shows, and re-shows, its
    // banner a second or two later; the last one shown is drawn on top). Keeps the focus in the field.
    let raisedOnce = false;
    let shows = 0;
    const raise = () => {
      try {
        if (typeof dialog.showModal !== 'function') return false;
        const typing = (root.activeElement || document.activeElement) === input;
        // At most MAX_SHOWS re-shows; never while the person is typing (closing the dialog drops a phone's keyboard).
        if (raisedOnce && (shows >= MAX_SHOWS || (typing && String(input.value || '') !== ''))) return false;
        if (raisedOnce) shows += 1;
        if (dialog.open) dialog.close();
        dialog.showModal();
        if (typing || !raisedOnce) input.focus();
        raisedOnce = true;
        return true;
      } catch (_) {
        return false;
      }
    };
    const watched = new Set();
    const watchPopovers = () => {
      try {
        Array.from(document.querySelectorAll('[popover]')).forEach((n) => {
          if (watched.has(n) || (typeof dialog.contains === 'function' && dialog.contains(n))) return;
          watched.add(n);
          n.addEventListener('toggle', (event) => {
            if (timer && event && event.newState === 'open') raise();
          });
          // already opened after this box (between two polls)
          if (raisedOnce && typeof n.matches === 'function' && n.matches(':popover-open')) raise();
        });
      } catch (_) {
        /* no popover support: nothing can cover it from the top layer */
      }
    };
    try {
      Array.from(document.querySelectorAll('[popover]')).forEach((n) => watched.add(n)); // shown before: below it
      watched.forEach((n) =>
        n.addEventListener('toggle', (event) => {
          if (timer && event && event.newState === 'open') raise();
        })
      );
    } catch (_) {
      /* see above */
    }
    // Consentmo's banner traps the focus by setting 'inert' on everything else on the page, this box included (live
    // probe, WebKit, 7 Oct 2026). The box takes it straight back off itself, at once (MutationObserver) or at the next
    // poll; the box is shown again on top so it keeps the focus.
    const uninert = () => {
      try {
        if (!timer || typeof dialog.hasAttribute !== 'function' || !dialog.hasAttribute('inert')) return false;
        dialog.removeAttribute('inert');
        raise();
        return true;
      } catch (_) {
        return false;
      }
    };
    try {
      if (typeof window.MutationObserver === 'function') {
        observer = new window.MutationObserver(uninert);
        observer.observe(dialog, { attributes: true, attributeFilter: ['inert'] });
      }
    } catch (_) {
      /* the poll below still checks */
    }
    raise();
    try {
      input.focus();
    } catch (_) {
      /* the person taps the field */
    }
    let ticks = 0;
    timer = setInterval(() => {
      ticks += 1;
      uninert();
      watchPopovers();
      if (ticks >= RAISE_TICKS && timer) {
        clearInterval(timer);
        timer = null;
      }
    }, POLL_MS);
  };
  if (quiet || setup) holdBack();
  try {
    if (setup) showSetup();
  } catch (_) {
    /* quiet mode never breaks the page */
  }

  // '?sofsy_internal=0' in a browser that was quiet: Consentmo forgets quiet mode's answer (it grants nothing).
  if (resetConsentmo) {
    watch(() => {
      const C = window.Consentmo;
      if (!C || typeof C.reset !== 'function') return false;
      try {
        const done = C.reset();
        if (done && typeof done.catch === 'function') done.catch(() => {});
      } catch (_) {
        /* Consentmo keeps its answer; the person can change it in its settings */
      }
      return true;
    });
  }
  if (!quiet) return;

  let announced = false;
  let lastLine = '';
  const log = (what) => {
    try {
      const line = announced ? `[sofsy] quiet mode: ${what}` : `[sofsy] quiet mode on (${what})`;
      if (what === lastLine) return;
      lastLine = what;
      announced = true;
      window.console.info(line);
    } catch (_) {
      /* no console */
    }
  };

  // 2. Check and correct.
  const startedAt = Date.now();
  let fixes = 0;
  let inFlight = false;
  let inFlightSince = 0;
  let consentmoHold = 0; // after an answer to Consentmo: no second answer before its push (an event) or this time
  const busy = () => {
    inFlight = true;
    inFlightSince = Date.now();
  };

  const consentNow = (api) => {
    try {
      return api.currentVisitorConsent() || null;
    } catch (_) {
      return null;
    }
  };
  const shopifyQuiet = (api) => {
    const c = consentNow(api);
    return !!c && c.analytics === 'no' && c.marketing === 'no' && c.sale_of_data === 'no';
  };
  const shopifySaysYes = (api) => {
    const c = consentNow(api);
    return !!c && (c.analytics === 'yes' || c.marketing === 'yes');
  };
  const saleOfDataNo = (api) => {
    const c = consentNow(api);
    return !!c && c.sale_of_data === 'no';
  };
  // Fails closed: if Consentmo's own getters throw, its answer cannot be read, so it is treated as "on" and answered
  // again (at most 3 times per page, 2 s apart). Treating it as quiet would leave a Consentmo that may push "yes" on
  // every page uncorrected; the cost of failing closed is a few extra answers to Consentmo on such a page.
  const consentmoOn = (C) => {
    try {
      return !!(C.analyticsAllowed() || C.marketingAllowed());
    } catch (_) {
      return true;
    }
  };
  // Quiet with Consentmo: Consentmo says "no" to analytics and marketing, and Shopify reads "no" to analytics,
  // marketing and sale of data (Consentmo's push carries only analytics, marketing and preferences).
  const consentmoQuiet = (C, api) => !consentmoOn(C) && !!api && shopifyQuiet(api);

  const setShopify = (api, done) => {
    if (!api) {
      log('consent could not be set; no consent API on the page');
      return;
    }
    busy();
    try {
      api.setTrackingConsent(Object.assign({}, QUIET_CONSENT), (result) => {
        inFlight = false;
        if (result && result.error) log(`consent could not be set (${String(result.error).slice(0, 80)})`);
        else log(done);
      });
    } catch (_) {
      inFlight = false;
      log('consent could not be set');
    }
  };

  const setConsentmo = (C, api) => {
    busy();
    let pending = null;
    try {
      pending = C.setTrackingConsent(Object.assign({}, CONSENTMO_ANSWER));
    } catch (_) {
      inFlight = false;
      setShopify(api, 'Consentmo refused; consent set off');
      return;
    }
    Promise.resolve(pending).then(
      () => {
        inFlight = false;
        consentmoHold = Date.now() + CONSENTMO_SETTLE_MS;
        log(fixes > 1 ? 'Consentmo turned consent back on; answered again' : 'consent set off through Consentmo');
      },
      () => {
        inFlight = false;
        setShopify(api, 'Consentmo refused; consent set off');
      }
    );
  };

  let okWritten = false;
  const confirmOk = () => {
    if (okWritten) return;
    okWritten = true;
    writeOk(true);
    if (setupLabel) setupLabel(true);
  };
  // Shopify or Consentmo reads not quiet (a flip back to "yes", sale of data not yet "no"): no confirmation until
  // it is "no" again. Deletes only a confirmation that exists.
  const unconfirm = () => {
    okWritten = false;
    if (readCookie(OK_COOKIE) !== null) writeOk(false);
    if (setupLabel) setupLabel(false);
  };
  const check = () => {
    if (inFlight) {
      if (Date.now() - inFlightSince < IN_FLIGHT_MAX_MS) return;
      inFlight = false; // a promise that never settles must not stop this page's checks
    }
    const C = consentmo();
    const api = shopifyApi();
    if (C) {
      if (consentmoQuiet(C, api)) {
        confirmOk();
        if (!announced) log('consent already off');
        return;
      }
      if (api) unconfirm(); // only once Shopify's answer can be read
      if (consentmoOn(C) || (api && shopifySaysYes(api))) {
        if (Date.now() < consentmoHold || fixes >= MAX_FIXES) return;
        fixes += 1;
        setConsentmo(C, api);
        return;
      }
      // Consentmo is quiet; Shopify's sale of data is not "no" yet (Consentmo never sends it): set it directly.
      if (!api || saleOfDataNo(api) || fixes >= MAX_FIXES) return;
      fixes += 1;
      setShopify(api, 'sale of data set off');
      return;
    }
    if (!api) return;
    if (shopifyQuiet(api)) {
      confirmOk();
      if (!announced) log('consent already off');
      return;
    }
    unconfirm();
    if (Date.now() - startedAt < CONSENTMO_GRACE_MS) return; // Consentmo may still be loading
    if (fixes >= MAX_FIXES) return;
    fixes += 1;
    setShopify(api, fixes > 1 ? 'consent was turned back on; set off again' : 'consent set off');
  };
  const safeCheck = () => {
    try {
      check();
    } catch (_) {
      /* quiet mode never breaks the page */
    }
  };

  try {
    // Shopify's "consent changed": Consentmo's push, a person's answer, or ours. Corrected if it is not quiet.
    document.addEventListener('visitorConsentCollected', () => {
      consentmoHold = 0; // Consentmo has pushed: its answer can be read now
      safeCheck();
    });
    if (!shopifyApi()) {
      const S = window.Shopify;
      if (S && typeof S.loadFeatures === 'function') {
        S.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], safeCheck);
      }
    }
  } catch (_) {
    /* the poll below still checks */
  }
  watch(
    () => {
      safeCheck();
      return false; // keep checking for the whole window: Consentmo pushes late
    },
    () => {
      if (!consentmo() && !shopifyApi()) log('the consent API never loaded');
    }
  );
})();
